import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box, CircularProgress } from "@mui/material";
import { useWatch } from "react-hook-form";

import {
  useFormMethodsContext,
  useFormSubmissionContext,
  useInitialValuesContext,
  useTemplateContext,
} from "../context";
import TemplateField from "../fields/components/TemplateField";

import {
  analyzeTemplateFields,
  collectVisibilityDependencies,
  FieldAnalysis,
  isFieldVisible,
} from "./formEngineUtils";
import { ExternalResources, FormData } from "./types";

interface FormEngineProps {
  onSubmit: (data: FormData) => void;
  onError?: (error: Error) => void;
  externalResources?: ExternalResources;
}

const FormEngine = ({
  onSubmit,
  onError,
  externalResources,
}: FormEngineProps): JSX.Element | null => {
  const { currentTemplate, isLoadingTemplate, templateError } =
    useTemplateContext();
  const { allOptionsLoaded } = useInitialValuesContext();
  const { registerSubmit, updateDirtyState, isPerformingInitialReset } =
    useFormSubmissionContext();
  const { formMethods } = useFormMethodsContext();

  const { control, getValues, handleSubmit, formState } = formMethods;

  // All hooks must be called at the top level
  const fieldAnalyses = useMemo<FieldAnalysis[]>(() => {
    if (!currentTemplate) {
      return [];
    }
    return analyzeTemplateFields(currentTemplate);
  }, [currentTemplate]);

  const visibilityDependencies = useMemo(() => {
    return collectVisibilityDependencies(fieldAnalyses);
  }, [fieldAnalyses]);

  const visibilityDepsWatch = useWatch({
    control,
    name: visibilityDependencies,
  });

  // State for field visibility
  const [fieldVisibility, setFieldVisibility] = useState<
    Record<string, boolean>
  >({});

  // Handle async field visibility resolution
  useEffect(() => {
    const resolveFieldVisibility = async () => {
      try {
        const visibilityPromises = fieldAnalyses.map(
          async ({ parameter: p, key }) => {
            const isVisible = await isFieldVisible(
              p,
              getValues,
              externalResources
            );
            return { key, isVisible };
          }
        );

        const visibilityResults = await Promise.all(visibilityPromises);
        const visibilityMap = visibilityResults.reduce(
          (acc, { key, isVisible }) => ({ ...acc, [key]: isVisible }),
          {}
        );

        setFieldVisibility(visibilityMap);
      } catch (error) {
        console.warn("Error resolving field visibility:", error);
        // Fallback to showing all fields
        const fallbackVisibility = fieldAnalyses.reduce(
          (acc, { key }) => ({ ...acc, [key]: true }),
          {}
        );
        setFieldVisibility(fallbackVisibility);
      }
    };

    resolveFieldVisibility();
  }, [fieldAnalyses, getValues, externalResources, visibilityDepsWatch]);

  const formContent = useMemo(() => {
    return fieldAnalyses
      .map(({ parameter: p, key, analysis }) => {
        const fieldIsVisible = fieldVisibility[key];
        if (!fieldIsVisible) {
          return null;
        }

        return (
          <Box
            key={key}
            sx={{
              width: {
                xs: "100%",
                sm:
                  "fullWidth" in p.display && p.display.fullWidth
                    ? "100%"
                    : "50%",
              },
              padding: 1,
              boxSizing: "border-box",
            }}
          >
            <TemplateField
              fieldParameter={p}
              externalResources={externalResources}
              fieldAnalysis={analysis}
            />
          </Box>
        );
      })
      .filter(Boolean);
  }, [fieldAnalyses, fieldVisibility, externalResources]);

  const handleFormSubmit = useCallback(
    (data: FormData) => {
      try {
        onSubmit(data);
      } catch (error) {
        console.error("[FormEngine] Error during form submission:", error);
        if (onError && error instanceof Error) {
          onError(error);
        }
      }
    },
    [onSubmit, onError]
  );

  // Register submit function with provider
  useEffect(() => {
    const submitFunction = () => handleSubmit(handleFormSubmit)();
    registerSubmit(submitFunction);
  }, [registerSubmit, handleSubmit, handleFormSubmit]);

  // Track the last dirty state to prevent unnecessary updates
  const lastDirtyStateRef = useRef<boolean>(false);

  // Update dirty state only when it actually changes and not during reset
  useEffect(() => {
    const currentIsDirty = formState.isDirty;
    const isCurrentlyResetting = isPerformingInitialReset;

    // Only update if:
    // 1. The dirty state has actually changed
    // 2. We're not currently performing a reset (which can cause transient dirty states)
    // 3. All options are loaded (form is fully initialized)
    if (
      lastDirtyStateRef.current !== currentIsDirty &&
      !isCurrentlyResetting &&
      allOptionsLoaded
    ) {
      lastDirtyStateRef.current = currentIsDirty;
      updateDirtyState(currentIsDirty);
    }
  }, [
    formState.isDirty,
    updateDirtyState,
    isPerformingInitialReset,
    allOptionsLoaded,
  ]);

  // Template switching is now handled automatically by useTemplateSwitching hook
  // via react-hook-form's watch mechanism - no manual triggering needed

  // Show loading while switching templates or if no template is loaded yet
  if (isLoadingTemplate || !currentTemplate) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 4,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Log error if template switching failed
  if (templateError) {
    console.error("[FormEngine] Error loading template");
    return null;
  }

  // Show loading while processing initial field options
  if (!allOptionsLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: 4,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          margin: -1,
        }}
      >
        {formContent}
      </Box>
    </form>
  );
};

export default React.memo(FormEngine);
